import { type DocumentSection_Content, DocumentSectionType, type DocumentTemplate } from '@alpod-ai-writer/shared';
import { yTextToSlateElement } from '@slate-yjs/core';
import { useEffect,useState } from 'react';
import { Element, Node } from 'slate';
import * as Y from 'yjs';

import { isElementWithType } from '../utils/editor.utils';
import { useDocumentEditor } from './useDocumentEditor.hook';

export interface CitationRef {
    sectionId: string;
    sectionTitle: string;
    headingId?: string;
    headingTitle?: string;
    path: number[];
}

export function useCitationRefs() {
    const editor = useDocumentEditor();
    const document = editor.yjsProvider.document;

    const [ citationRefs, setCitationRefs ] = useState(getRefsFromDocument(editor.template, document));

    useEffect(() => {
        let debounceTimer: ReturnType<typeof setTimeout>|null = null;

        const handleUpdate = () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            debounceTimer = setTimeout(() => {
                const newState = getRefsFromDocument(editor.template, document);
                setCitationRefs(newState);
            }, 300);
        };

        document.on('updateV2', handleUpdate);
        handleUpdate();

        return () => {
            document.off('updateV2', handleUpdate);
            
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [ document ]);

    return { citationRefs };
}

function getRefsFromDocument(template: DocumentTemplate, document: Y.Doc) {
    const refs = new Map<string, CitationRef[]>();
    let lastHeadingId: string|undefined;
    let lastHeadingLabel: string|undefined;

    const findRef = (sectionId: string, sectionTitle: string, path: number[], elements: unknown[]) => {
        elements.forEach((element, index) => {
            if (isElementWithType(element)) {
                const _path = [ ...path, index ];

                switch (element.type) {
                    case 'citation': {
                        const ref = (element as unknown as { ref: string }).ref as string;

                        if (!refs.has(ref)) {
                            refs.set(ref, []);
                        }

                        refs.get(ref)?.push({
                            sectionId,
                            sectionTitle,
                            headingId: lastHeadingId,
                            headingTitle: lastHeadingLabel,
                            path: _path
                        });

                        break;
                    }
                    case 'h1':
                    case 'h2':
                    case 'h3': {
                        lastHeadingId = element.id;
                        lastHeadingLabel = Node.string(element as Element);
                        break;
                    }
                }

                if (element.children.length > 0) {
                    findRef(sectionId, sectionTitle, _path, element.children);
                }
            }
        });
    };

    template.config.sections
        .filter((section): section is DocumentSection_Content => section.type === DocumentSectionType.Content)
        .forEach(section => {
            if (!section.content) {
                return;
            }

            const docContent = document.get(section.content.id, Y.XmlText);

            if (docContent instanceof Y.XmlText) {
                const slateContent = yTextToSlateElement(docContent);

                lastHeadingId = undefined;
                lastHeadingLabel = undefined;

                findRef(section.id, section.title, [], slateContent.children);
            }
        });

    return refs;
}
